import { Disclosure } from '@headlessui/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/outline';
import BlockContent from '@sanity/block-content-to-react';
import { AnimatePresence, motion } from 'framer-motion';
import { StaticImage } from 'gatsby-plugin-image';
import * as React from 'react';

import { ISanityFaqs } from '../../fragments/sanity-frequently-asked-questions-parts';

interface FAQsProps {
  frequentlyAskedQuestions: ISanityFaqs;
}

function FAQs({ frequentlyAskedQuestions }: FAQsProps): JSX.Element {
  return (
    <div className="relative bg-secondary">
      {frequentlyAskedQuestions.title.toLowerCase() === 'the rules' && (
        <div className="absolute -left-24 top-48">
          <StaticImage
            src="../../images/assets/steps-1.png"
            alt=""
            width={400}
          />
        </div>
      )}
      {frequentlyAskedQuestions.title.toLowerCase() === 'the game' && (
        <div className="absolute -right-28 -top-40">
          <StaticImage
            src="../../images/assets/steps-1.png"
            alt=""
            width={200}
          />
        </div>
      )}
      <div className="py-12 lg:py-16 px-4 mx-auto max-w-screen-2xl sm:px-6 lg:px-8 md:space-x-10 relative">
        <div className="mx-auto">
          <h2 className="text-xl md:text-3xl font-bold text-left uppercase text-light sm:text-sm">
            {frequentlyAskedQuestions.title}
          </h2>
          <dl className="mt-3 w-11/12 mx-auto">
            {frequentlyAskedQuestions.faqs.map((faq, index) => (
              <Disclosure as="div" key={faq.question} className="">
                {({ open }) => (
                  <>
                    <dt className="text-lg">
                      <Disclosure.Button className="flex items-start w-full boder-solid py-8 border-gray-400 border-b space-x-2 text-left text-light">
                        <span className="flex items-center h-7">
                          <PlusIcon
                            className="w-6 h-6 text-light"
                            aria-hidden="true"
                          />
                        </span>
                        <span className="font-semibold text-xs md:text-sm">
                          {faq.question}
                        </span>
                      </Disclosure.Button>
                    </dt>
                    <AnimatePresence>
                      {open && (
                        <Disclosure.Panel
                          static
                          as={motion.dd}
                          initial="collapsed"
                          animate="open"
                          exit="collapsed"
                          variants={{
                            open: { opacity: 1, height: 'auto' },
                            collapsed: { opacity: 0, height: 0 },
                          }}
                          transition={{ duration: 0.15 }}
                          className="pr-12 overflow-y-hidden  boder-solid py-8 border-gray-400 border-b"
                        >
                          <div className="relative">
                            <MinusIcon
                              className="mr-3 w-6 h-10 text-primary absolute"
                              aria-hidden="true"
                            />
                            <BlockContent
                              renderContainerOnSingleChild
                              blocks={faq._rawAnswer}
                              className="text-xs md:text-base leading-10 ml-8"
                            />
                          </div>
                        </Disclosure.Panel>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </Disclosure>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

export { FAQs };
